import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { NSUnitTrainConstants } from '@ruby/configs/unit-train.constants';
import {
  IUnitTrainFilters,
  IUTFiltersConfigurations,
  IUTFiltersExtraConfigurations,
  IUTGridData,
  IUTLoadTrainRequest,
  IUTReleaseCars
} from '@ruby/modules/unit-train/models/unit-train.interface';
import * as UTAdvanceSearchActions from '@ruby/modules/unit-train/store/actions/ut-advance-search.actions';
import * as fromUTAdvanceSearch from '@ruby/modules/unit-train/store/reducers/ut-advance-search.reducer';
import * as UTAdvanceSearchSelectors from '@ruby/modules/unit-train/store/selectors/ut-advance-search.selectors';
import { LoadTrainConfirmationContainer } from '@ruby/shared/dialogs/unit-train/load-train-confirmation/containers/load-train-confirmation.container';
import { EUTGridType } from '@ruby/shared/enums/unit-train.enums';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';

@Component({
  selector: 'ng-ut-results-grid-container',
  templateUrl: './ut-results-grid.container.html',
  styleUrls: ['./ut-results-grid.container.scss']
})
export class UTResultsGridContainer implements OnDestroy {
  @Output() next = new EventEmitter<Partial<IUTGridData>>();
  gridConfigs!: IGridConfigurations;
  filtersConfigs!: Partial<IUTFiltersConfigurations>;
  filtersExtraConfigs?: Partial<IUTFiltersExtraConfigurations>;
  type!: EUTGridType;
  store$: Observable<fromUTAdvanceSearch.State>;
  data: Array<Partial<IUTGridData>> = [];
  gridInitialized = false;
  private filters?: IUnitTrainFilters;
  private loadTrain?: IUTLoadTrainRequest;

  constructor(private store: Store<fromUTAdvanceSearch.State>, private dialog: MatDialog) {
    this.store$ = this.store.select(UTAdvanceSearchSelectors.selectUTAdvanceSearchType).pipe(
      tap((state: { type: EUTGridType }) => {
        this.type = state.type;
        this.gridConfigs = NSUnitTrainConstants.utGridConfigs[state.type];
        this.filtersConfigs = NSUnitTrainConstants.utFiltersConfigs[state.type];
        this.filtersExtraConfigs = NSUnitTrainConstants.utFiltersExtraConfigs[state.type];
        this.gridInitialized = false;
        setTimeout(() => {
          this.gridInitialized = true;
        }, 1);
      }),
      switchMap(() => this.store.select(UTAdvanceSearchSelectors.selectUTAdvanceSearchState)),
      tap(this.setupDataByType.bind(this))
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(UTAdvanceSearchActions.resetAdvanceSearchState());
  }

  /**
   * Make Advance Search
   *
   * @summary emit an event to make a search
   * @param $filters: IUnitTrainFilters
   * @return void
   */
  makeAdvanceSearch($filters: IUnitTrainFilters): void {
    this.filters = $filters;
    switch (this.type) {
      case EUTGridType.SHIPMENT_LISTING:
        this.store.dispatch(UTAdvanceSearchActions.getShipmentListing({ data: $filters }));
        break;
      case EUTGridType.FLEET_INQUIRY:
        this.store.dispatch(UTAdvanceSearchActions.getFleetInquiry({ data: $filters }));
        break;
      case EUTGridType.LOAD_TRAIN:
        this.store.dispatch(UTAdvanceSearchActions.getLoadTrain({ data: $filters }));
        break;
      case EUTGridType.TRAIN_CONSIST:
        if ($filters.carId) {
          const splitedCarId: Array<string> = $filters.carId.split(' ');
          this.store.dispatch(UTAdvanceSearchActions.getTrainConsistByCar({
            data: { initial: splitedCarId[0], number: splitedCarId[1], scanner: !!$filters.scannerValue }
          }));
        } else {
          this.store.dispatch(UTAdvanceSearchActions.getTrainConsistByOriginDate({
            data: { originDate: $filters.trainOriginDate!, trainSymbol: $filters.trainSymbol! }
          }));
        }
        break;
      case EUTGridType.RELEASE_EMPTIES:
        this.store.dispatch(UTAdvanceSearchActions.getReleaseEmpties({ data: $filters }));
        break;
      case EUTGridType.SHIPMENT_STATUS:
        this.store.dispatch(UTAdvanceSearchActions.getShipmentStatus({ data: $filters }));
        break;
      case EUTGridType.TRAIN_SET_INQUIRY:
        this.store.dispatch(UTAdvanceSearchActions.getTrainSetInquiry({ data: $filters }));
        break;
      case EUTGridType.HOPPER_RUN:
        this.store.dispatch(UTAdvanceSearchActions.getHopperRun({ data: $filters }));
        break;
      case EUTGridType.TRAIN_SIZE_PERFORMANCE:
        this.store.dispatch(UTAdvanceSearchActions.getTrainSizePerformance({ data: $filters }));
        break;
    }
  }

  /**
   * Setup Data By Type
   *
   * @summary Set data value base on grid type
   * @param state: fromUTAdvanceSearch.State
   * @return void
   */
  setupDataByType(state: fromUTAdvanceSearch.State): void {
    switch (this.type) {
      case EUTGridType.SHIPMENT_LISTING:
        this.data = state.shipmentListing;
        break;
      case EUTGridType.FLEET_INQUIRY:
        this.data = state.fleetInquiry;
        break;
      case EUTGridType.LOAD_TRAIN:
        this.loadTrain = state.loadTrain;
        this.data = state.loadTrain?.loadTrainLoadedPermits || [];
        break;
      case EUTGridType.TRAIN_CONSIST:
        this.data = state.trainConsist?.historicalConsist as Array<Partial<IUTGridData>> || [];
        break;
      case EUTGridType.RELEASE_EMPTIES:
        this.data = state.emptyCars;
        break;
      case EUTGridType.SHIPMENT_STATUS:
        this.data = state.shipmentStatus;
        break;
      case EUTGridType.TRAIN_SET_INQUIRY:
        this.data = state.trainSetInquiry;
        break;
      case EUTGridType.HOPPER_RUN:
        this.data = state.hopperRun;
        break;
      case EUTGridType.TRAIN_SIZE_PERFORMANCE:
        this.data = state.trainSizePerformance;
        break;
      default:
        this.data = [];
    }
  }

  /**
   * Confirmation Step
   *
   * @summary opens the confirmation step
   * @param $event: Patial<IUTGridData>
   * @returns void
   */
  confirmationStep($event: Partial<IUTGridData>) {
    this.dialog.open(LoadTrainConfirmationContainer, {
      data: { record: $event, loadTrain: this.loadTrain },
      width: '100vw',
      maxWidth: '100vw',
      height: 'calc(100% - 169px)',
      position: { bottom: '0px' }
    });
  }

  /**
   * Release Cars
   *
   * @summary will release selected cars
   * @param $event: Patial<IUTReleaseCars>
   * @returns void
   */
  releaseCars($event: Partial<IUTReleaseCars>): void {
    this.store.dispatch(UTAdvanceSearchActions.releaseCars({ data: { carsToRelease: $event, filters: this.filters! } }));
  }
}
